import { el, clear } from '../lib/dom.js';
import { getRegionIndex, getRegion, getLocation, getLocationArea, mapLimit } from '../data/api.js';
import { loadSnapshot } from '../data/snapshot.js';
import { prettify } from '../data/pokedex-resolver.js';
import { settings } from '../store/settings.js';
import { Sprite } from '../components/Sprite.js';

const idFromUrl = (u) => Number(u.replace(/\/$/, '').split('/').pop());

function errorView(title, err, backHref, backLabel) {
  const view = el('section', { class: 'view lookup' });
  view.append(
    el(
      'div',
      { class: 'view--error' },
      el('h1', {}, title),
      el('p', {}, navigator.onLine ? err.message : 'You appear to be offline.'),
      el('a', { class: 'button', href: backHref }, backLabel),
    ),
  );
  return view;
}

/** #/locations — every region PokéAPI knows about. */
export async function RegionsView() {
  const view = el('section', { class: 'view lookup' });
  view.append(el('div', { class: 'view--loading' }, 'Loading regions…'));

  let idx;
  try {
    idx = await getRegionIndex();
  } catch (err) {
    return errorView('Could not load regions', err, '#/search', 'Back to lookup');
  }

  clear(view).append(
    el('nav', { class: 'lookup__nav' }, el('a', { class: 'link', href: '#/search' }, '‹ Lookup')),
    el(
      'header',
      { class: 'view__header' },
      el('h1', {}, 'Regions'),
      el('p', {}, 'Browse by place instead of by Pokémon — pick a region to see its locations.'),
    ),
    el(
      'div',
      { class: 'search__results' },
      ...idx.results.map((r) =>
        el(
          'a',
          { class: 'search__row', href: `#/region/${r.name}` },
          el('span', { class: 'search__name' }, prettify(r.name)),
        ),
      ),
    ),
  );
  return view;
}

/** #/region/:name — the locations within one region. */
export async function RegionView(name) {
  const view = el('section', { class: 'view lookup' });
  view.append(el('div', { class: 'view--loading' }, 'Loading region…'));

  let d;
  try {
    d = await getRegion(name);
  } catch (err) {
    return errorView('Could not load this region', err, '#/locations', '‹ Regions');
  }

  const locations = [...d.locations].sort((a, b) => a.name.localeCompare(b.name));

  clear(view).append(
    el(
      'nav',
      { class: 'lookup__nav' },
      el('a', { class: 'link', href: '#/locations' }, '‹ Regions'),
    ),
    el(
      'header',
      { class: 'view__header' },
      el('h1', {}, prettify(d.name)),
      el('p', {}, `${locations.length} location${locations.length === 1 ? '' : 's'}.`),
    ),
    el(
      'div',
      { class: 'search__results' },
      ...locations.map((l) =>
        el(
          'a',
          { class: 'search__row', href: `#/location/${l.name}` },
          el('span', { class: 'search__name' }, prettify(l.name)),
        ),
      ),
    ),
  );
  return view;
}

/** #/location/:name — every Pokémon encountered here, for the active game. */
export async function LocationView(name) {
  const view = el('section', { class: 'view lookup' });
  view.append(el('div', { class: 'view--loading' }, 'Loading location…'));

  const snap = await loadSnapshot();

  let loc;
  try {
    loc = await getLocation(name);
  } catch (err) {
    return errorView('Could not load this location', err, '#/locations', '‹ Regions');
  }

  const areas = loc.areas || [];
  const areaData = await mapLimit(areas, 6, (a) => getLocationArea(a.name).catch(() => null));

  const st = settings.get();
  const vg = st.versionGroup;
  const versions = new Set(snap.versionGroupByKey.get(vg)?.versions || []);

  // Merge encounters across every area of this location into one
  // per-species row — the same Pokémon often turns up in several areas
  // (1F, B1F, …) and that's noise the visitor doesn't need.
  const bySpecies = new Map();
  for (const area of areaData) {
    if (!area) continue;
    for (const enc of area.pokemon_encounters || []) {
      const details = (enc.version_details || []).filter(
        (v) => vg === 'all' || !versions.size || versions.has(v.version.name),
      );
      if (!details.length) continue;
      const id = idFromUrl(enc.pokemon.url);
      const sp = snap.speciesById.get(id);
      if (!sp) continue;
      const methods = bySpecies.get(id)?.methods || new Set();
      for (const v of details) {
        for (const d of v.encounter_details) methods.add(prettify(d.method?.name || 'Walk'));
      }
      bySpecies.set(id, { species: sp, methods });
    }
  }
  const found = [...bySpecies.values()].sort((a, b) => a.species.id - b.species.id);

  const style = st.spriteStyle;
  const regionName = loc.region?.name ? prettify(loc.region.name) : null;
  const regionLink = loc.region?.name
    ? el('a', { class: 'link', href: `#/region/${loc.region.name}` }, `‹ ${regionName}`)
    : el('a', { class: 'link', href: '#/locations' }, '‹ Regions');

  clear(view).append(
    el('nav', { class: 'lookup__nav' }, regionLink),
    el(
      'header',
      { class: 'view__header' },
      el('h1', {}, prettify(loc.name)),
      el(
        'p',
        {},
        found.length
          ? `${found.length} Pokémon found here${vg === 'all' ? '' : ` in ${prettify(vg)}`}.`
          : `No wild encounters on record here${vg === 'all' ? '' : ` for ${prettify(vg)}`}.`,
      ),
    ),
    el(
      'div',
      { class: 'ab-mons' },
      ...found.map(({ species, methods }) =>
        el(
          'a',
          { class: 'ab-mon', href: `#/pokemon/${species.id}`, title: [...methods].join(', ') },
          Sprite(species.id, { style, alt: species.name, size: 48 }),
          el('span', {}, species.name.replace(/-/g, ' ')),
        ),
      ),
    ),
  );
  return view;
}
